import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  NakedButton,
  PopoverDialog,
  PopoverMenuItem,
  SearchField,
  Text,
  useFormField,
  usePopoverDialog,
} from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';

type AddLocationProps = {
  locationIds: string[];
  maxWidth?: number;
  onAdd?: (locationId: string) => void;
};

export const AddLocationButton = ({ locationIds, maxWidth, onAdd }: AddLocationProps) => {
  const { t } = useTranslation('schedule');
  const { getScopeName } = useSchedulingLocationInfo();
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({ placement: 'bottom' });
  const searchField = useFormField({ type: 'text', value: '' });

  const locationOptions = useMemo(() => {
    const locationsArr = locationIds?.map((locationId) => ({
      key: locationId,
      label: getScopeName(locationId) ?? '',
    }));

    if (!searchField.value.trim()) return locationsArr;

    return locationsArr?.filter(({ label }) => label.toLowerCase().includes(searchField.value.toLowerCase()));
  }, [getScopeName, locationIds, searchField.value]);

  return (
    <>
      <NakedButton css={containerStyle(maxWidth)} {...getTriggerProps()}>
        <div css={plusIconStyle}>
          <Icon name='plus' color='primary' size={16} />
        </div>
        <Text size='medium' color='subdued'>
          {t('Add Location')}
        </Text>
      </NakedButton>
      <PopoverDialog {...getDialogProps()} css={{ padding: 0 }}>
        <SearchField {...searchField} name='dropdown-search' autoFocus css={{ margin: theme.spacing(1) }} />
        {locationOptions?.map((option) => (
          <PopoverMenuItem key={option.key} onClick={() => onAdd?.(option.key)}>
            {option.label}
          </PopoverMenuItem>
        ))}
      </PopoverDialog>
    </>
  );
};

const containerStyle = (maxWidth?: number) =>
  css({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'none',
    border: `1px dashed ${theme.colors.primary20}`,
    borderRadius: theme.borderRadius.small,
    padding: theme.spacing(1, 1),
    gap: theme.spacing(1),
    maxWidth: maxWidth ? `${maxWidth}px` : 'auto',
  });

const plusIconStyle = css({
  width: 24,
  height: 24,
  borderRadius: theme.borderRadius.full,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.colors.primary10,
});
